import React, { useEffect, useState } from 'react';
import { Briefcase, Users, TrendingUp, Clock, LayoutDashboard, ArrowUpRight } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { jobBoardApi } from '../../api/jobBoardApi';
import { ApplicationStatus, JobBoardStats } from '../../types/jobBoard';
import Spinner from '../../components/Spinner';

const OverviewDashboard = () => {
  const [stats, setStats] = useState<JobBoardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await jobBoardApi.getOverviewStats();
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching overview stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const COLORS: Record<string, string> = {
    Applied: '#6366f1',
    UnderReview: '#f59e0b',
    InterviewScheduled: '#8b5cf6',
    Offered: '#ec4899',
    Rejected: '#ef4444',
    Hired: '#10b981',
  };

  const statusKeys: ApplicationStatus[] = ['Applied', 'UnderReview', 'InterviewScheduled', 'Offered', 'Rejected', 'Hired'];
  const chartData = statusKeys.map(key => ({
    status: key.replace(/([A-Z])/g, ' $1').trim(),
    name: stats?.applicantsByStatus?.[key] || 0,
    color: COLORS[key] || '#6366f1'
  }));

  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;
  if (!stats) return <div className="p-8 text-center text-slate-500">Dashboard data unavailable.</div>;

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      <header className="mt-5 mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <LayoutDashboard className="text-indigo-600" size={24} />
            Job Board Overview
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Real-time summary of recruitment health and candidate flow.
          </p>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Active Jobs', value: stats?.activeJobs?.toString() || '0', icon: Briefcase, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Total Applicants', value: stats?.totalApplicants?.toLocaleString() || '0', icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Total Postings', value: stats?.totalJobs?.toString() || '0', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Open Vacancies', value: stats?.jobsNeedingApplicants?.toString() || '0', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 transition-all hover:shadow-md flex items-center gap-4">
            <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center shrink-0`}>
              <stat.icon className={stat.color} size={24} />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{stat.label}</p>
              <h3 className="text-2xl font-bold text-slate-800 leading-none">{stat.value}</h3>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <section className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 min-w-0 overflow-hidden">
          <h2 className="text-lg font-semibold mb-6 flex items-center gap-2 text-slate-800"><TrendingUp size={18} className="text-indigo-500" /> Applicant Pipeline</h2>
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="name"
                  nameKey="status"
                  animationDuration={1000}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mt-4 px-2">
            {chartData.map(item => (
              <div key={item.status} className="flex items-center gap-2 whitespace-nowrap">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ background: item.color }}></div>
                <span className="text-sm font-medium text-slate-600">{item.status}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <h2 className="text-lg font-semibold mb-4 text-slate-800">Recent Activity</h2>
          <div className="space-y-4">
            {stats.recentApplications && stats.recentApplications.length > 0 ? stats.recentApplications.slice(0, 5).map((app) => (
              <div key={app.applicantId} className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 border border-indigo-100">
                  <Users size={14} className="text-slate-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-slate-800">{app.fullName}</p>
                  <p className="text-[10px] text-slate-400 font-mono">Job: {app.jobId.slice(0, 8)}</p>
                  <p className="text-xs font-semibold text-indigo-600">{app.applicationStatus}</p>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-400 text-sm italic">
                No recent activity found.
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-800">Active Jobs</h2>
            <button onClick={() => navigate('/job-board/jobs')} className="text-indigo-600 text-sm font-medium hover:underline flex items-center gap-1">
              View all <ArrowUpRight size={14} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Job Title</th>
                  <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Applicants</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentPostings && stats.recentPostings.length > 0 ? stats.recentPostings.map((job) => (
                  <tr key={job.jobId} className="group border-b border-slate-50 last:border-0">
                    <td className="py-4 text-sm font-medium text-slate-800 group-hover:text-indigo-600 transition-colors">
                      <Link to={`/job-board/jobs/${job.jobId}`}>{job.jobTitle}</Link>
                    </td>
                    <td className="py-4 text-sm text-slate-500 text-right font-semibold">{job.applicants ? job.applicants.length : 0}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={2} className="py-8 text-center text-slate-400 text-sm italic">No active jobs found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-800">Recent Applications</h2>
          </div>
          <div className="space-y-4">
            {(stats?.recentApplications || []).length > 0 ? stats.recentApplications.map((app) => (
              <div key={app.applicantId} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                <div className="text-left">
                  <p className="text-sm font-bold text-slate-800">{app.fullName}</p>
                  <p className="text-xs text-slate-500">{app.applicationStatus}</p>
                </div>
                <span className="text-[10px] font-mono font-medium text-slate-400 bg-white px-2 py-1 rounded-lg border border-slate-100">ID: {app.applicantId.slice(0, 8)}</span>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-400 text-sm italic">
                No recent applications found.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default OverviewDashboard;